import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtNum } from "@/lib/format";
import { AlertTriangle, CheckCircle2, XCircle, ShieldAlert, Search, Fingerprint, FileX } from "lucide-react";
import type { AuditSeverity } from "@/lib/fiscal-types";

const SEV_STYLES: Record<AuditSeverity, { bg: string; text: string; ring: string; label: string }> = {
  CRITICA: { bg: "bg-red-500/15", text: "text-red-300", ring: "ring-red-500/40", label: "CRÍTICA" },
  ALTA:    { bg: "bg-orange-500/15", text: "text-orange-300", ring: "ring-orange-500/40", label: "ALTA" },
  MEDIA:   { bg: "bg-amber-500/15", text: "text-amber-300", ring: "ring-amber-500/40", label: "MÉDIA" },
  BAIXA:   { bg: "bg-yellow-500/15", text: "text-yellow-300", ring: "ring-yellow-500/40", label: "BAIXA" },
  INFO:    { bg: "bg-sky-500/15", text: "text-sky-300", ring: "ring-sky-500/40", label: "INFO" },
};

function SevBadge({ s }: { s: AuditSeverity }) {
  const sty = SEV_STYLES[s];
  return (
    <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black tracking-widest ring-1 bg-opacity-10 ${sty.bg} ${sty.text} ${sty.ring} uppercase shadow-sm`}>
      {sty.label}
    </span>
  );
}

export default function ValidacaoXmlPage() {
  const { notas, dashboard } = useFiscalStore();
  const [q, setQ] = useState("");
  const [filtroSev, setFiltroSev] = useState<AuditSeverity | "all">("all");

  useEffect(() => { document.title = "Validação de XML — Extrator Fiscal"; }, []);

  const { validas, comErro, estruturaFindings } = useMemo(() => {
    const CATS_ESTRUTURA = new Set(["ESTRUTURA", "PROTOCOLO", "INTEGRIDADE"]);
    let validas = 0, comErro = 0;
    const estruturaFindings: { nota: typeof notas[0]; findings: typeof notas[0]["auditFindings"] }[] = [];

    for (const nota of notas) {
      const findings = (nota.auditFindings ?? []).filter((f) => CATS_ESTRUTURA.has(f.categoria));
      if (findings.length > 0) {
        comErro++;
        estruturaFindings.push({ nota, findings });
      } else {
        validas++;
      }
    }
    return { validas, comErro, estruturaFindings };
  }, [notas]);

  const filtrados = useMemo(() => {
    const ql = q.toLowerCase();
    return estruturaFindings.filter(({ nota, findings }) => {
      if (filtroSev !== "all" && !findings.some((f) => f.severidade === filtroSev)) return false;
      if (ql) return nota.chave.toLowerCase().includes(ql) || nota.emitente.toLowerCase().includes(ql) || (nota.numero || "").includes(ql);
      return true;
    });
  }, [estruturaFindings, q, filtroSev]);

  const totalFindings = estruturaFindings.reduce((s, { findings }) => s + findings.length, 0);

  if (!dashboard) return <Navigate to="/upload" replace />;

  return (
    <div className="px-10 py-10 max-w-7xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header className="flex items-center gap-5">
        <div className="size-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.15)]">
          <ShieldAlert className="size-7 text-blue-500" />
        </div>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white dark:text-glow">Validação de XML</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 font-medium italic">
            Inspeção técnica de estrutura, protocolo e integridade dos arquivos importados.
          </p>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-6">
        {[
          { label: "XMLs válidos", value: fmtNum(validas), icon: CheckCircle2, color: "from-emerald-500/20 to-emerald-600/5", textColor: "text-emerald-400", glow: "shadow-[0_0_15px_rgba(52,211,153,0.15)]", delay: '100ms' },
          { label: "Com problemas", value: fmtNum(comErro), icon: XCircle, color: "from-red-500/20 to-red-600/5", textColor: "text-red-400", glow: "shadow-[0_0_15px_rgba(248,113,113,0.15)]", delay: '200ms' },
          { label: "Total de achados", value: fmtNum(totalFindings), icon: ShieldAlert, color: "from-amber-500/20 to-amber-600/5", textColor: "text-amber-400", glow: "shadow-[0_0_15px_rgba(251,169,76,0.15)]", delay: '300ms' },
          { label: "Total processados", value: fmtNum(notas.length), icon: Fingerprint, color: "from-blue-500/20 to-blue-600/5", textColor: "text-[#33CCFF]", glow: "shadow-[0_0_15px_rgba(51,204,255,0.15)]", delay: '400ms' },
        ].map((kpi) => (
          <div key={kpi.label} className={`glass-card group relative overflow-hidden rounded-3xl p-6 border border-white/10 bg-gradient-to-br ${kpi.color} backdrop-blur-xl ${kpi.glow} hover-lift animate-in-card`} style={{ animationDelay: kpi.delay }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400/70">{kpi.label}</div>
              <kpi.icon className={`size-5 ${kpi.textColor} opacity-80 group-hover:scale-110 transition-transform`} />
            </div>
            <div className={`text-4xl font-black tabular-nums ${kpi.textColor}`}>{kpi.value}</div>
            <div className="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
              <kpi.icon className="size-24 rotate-12" />
            </div>
          </div>
        ))}
      </div>

      {/* Banner de status geral */}
      <div className={`rounded-3xl p-7 flex items-center gap-7 border-2 backdrop-blur-3xl transition-all duration-500 shadow-2xl animate-in-card ${
        comErro === 0 
          ? "bg-emerald-500/15 border-emerald-500/30 shadow-[0_12px_40px_rgba(16,185,129,0.15)] ring-1 ring-emerald-400/20" 
          : "bg-amber-500/15 border-amber-500/30 shadow-[0_12px_40px_rgba(245,158,11,0.15)] ring-1 ring-amber-400/20"
      }`} style={{ animationDelay: '500ms' }}>
        <div className={`size-14 rounded-2xl flex items-center justify-center shrink-0 shadow-lg ${
          comErro === 0 ? "bg-emerald-500/20 text-emerald-400 border border-emerald-400/30" : "bg-amber-500/20 text-amber-400 border border-amber-400/30"
        }`}>
          {comErro === 0 ? <CheckCircle2 className="size-8" /> : <AlertTriangle className="size-8" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-2xl font-black tracking-tight ${comErro === 0 ? "text-emerald-400 dark:text-glow-green" : "text-amber-400 dark:text-glow-amber"}`}>
            {comErro === 0 ? "Nenhum erro de integridade detectado" : `${fmtNum(comErro)} XMLs requerem atenção técnica`}
          </div>
          <div className="text-sm text-slate-700 dark:text-slate-300 mt-1.5 font-semibold leading-relaxed">
            {comErro === 0
              ? "Excelente! Todos os arquivos processados estão em conformidade com as regras de estrutura e protocolo da SEFAZ."
              : "Atenção: Foram identificados problemas que podem invalidar ou atrasar o processamento destes documentos. Verifique o detalhamento abaixo."}
          </div>
        </div>
        {comErro > 0 && (
          <div className="hidden lg:block px-5 py-2.5 bg-amber-500/20 border-2 border-amber-500/40 rounded-2xl text-[11px] font-black text-amber-400 uppercase tracking-[0.2em] shadow-lg animate-pulse">
            Ação Requerida
          </div>
        )}
      </div>

      {/* Filtros */}
      {estruturaFindings.length > 0 && (
        <>
          <div className="flex gap-4 flex-wrap items-center bg-white/5 p-2 rounded-2xl border border-white/5 backdrop-blur-md shadow-inner animate-in-card" style={{ animationDelay: '600ms' }}>
            <div className="relative max-w-md flex-1">
              <Search className="size-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Filtrar por chave, número ou emitente…"
                className="w-full pl-11 pr-4 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white/10 transition-all font-medium" />
            </div>
            <div className="flex gap-2 p-1 bg-black/20 rounded-xl border border-white/5">
              {(["all", "CRITICA", "ALTA", "MEDIA", "BAIXA", "INFO"] as const).map((s) => {
                const sty = s === "all" ? null : SEV_STYLES[s];
                return (
                  <button key={s} onClick={() => setFiltroSev(s)}
                    className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all duration-300 ${
                      filtroSev === s 
                        ? (sty ? `${sty.bg} ${sty.text} ring-1 ${sty.ring} shadow-lg` : "bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]") 
                        : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                    }`}>
                    {s === "all" ? "Todos" : SEV_STYLES[s].label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4">
            {filtrados.map(({ nota, findings }) => (
              <div key={nota.chave} className="group relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden hover:bg-white/[0.05] hover:border-white/20 transition-all duration-300 shadow-lg">
                <div className="px-6 py-4 flex items-center justify-between gap-6">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="size-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
                      <FileX className="size-5 text-red-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <span>NF {nota.numero}/{nota.serie}</span>
                        <span className="text-slate-400 font-normal">|</span>
                        <span className="truncate opacity-90">{nota.emitente || "(Emitente não identificado)"}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5 tracking-tight">{nota.chave}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-[10px] font-bold text-red-400 uppercase tracking-tighter">
                      {findings.length} Incidência(s)
                    </div>
                  </div>
                </div>
                <div className="px-6 pb-5 pt-1 space-y-3">
                  {findings.map((f, i) => (
                    <div key={i} className="flex items-start gap-4 p-3 rounded-xl bg-black/10 border border-white/5">
                      <div className="mt-0.5"><SevBadge s={f.severidade} /></div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 text-xs">
                          <span className="font-mono text-blue-400 font-bold bg-blue-400/10 px-1.5 rounded uppercase">{f.regra}</span>
                          <span className="text-slate-700 dark:text-slate-300 font-medium leading-relaxed">{f.mensagem}</span>
                        </div>
                        {f.campo && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Localização:</span>
                            <span className="font-mono text-[10px] text-slate-400 bg-white/5 px-2 py-0.5 rounded border border-white/5">{f.campo}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {filtrados.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
                <div className="size-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="size-8 text-emerald-400 opacity-50" />
                </div>
                <p className="text-slate-400 font-medium">Nenhum resultado encontrado para os filtros aplicados.</p>
                <button onClick={() => { setQ(""); setFiltroSev("all"); }} className="mt-4 text-xs font-bold text-blue-400 hover:text-blue-300 underline underline-offset-4">Limpar todos os filtros</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
